import React, { useState, useEffect, useMemo } from 'react';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Storefront from '../../services/Storefront';
import Box from '@mui/material/Box';
import Loading from '../Loading';
import Button from '@mui/material/Button';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import Paper from '@mui/material/Paper';
import Notifications from '../../services/Notifications';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import { ThemeProvider } from '@mui/material/styles';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import Typography from '@mui/material/Typography';
import MUIModal from '../shared/MUIModal';
import MUIAlertModal from '../shared/MUIAlertModal';


export const ProductSalesCodeAdministration = () => {
    const [rows, setRows] = useState([]);
    const [fetching, setFetching] = useState(false);
    const localized = useStorefrontAdministrationConfig().labels;
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const theme = StorefrontAdminTheme;
    const responseMessages = {
        "DuplicateKeyViolation": localized.ProductSalesCodeDuplicateCodeViolation ||'Duplicate Code Violation',
        "GenericFailure": localized.ProductSalesCodeGenericFailure ||'Generic Failure',
        "DeleteSuccess": localized.ProductSalesCodeDeleteSuccess ||'Delete Success',
        "UpdateSuccess": localized.ProductSalesCodeUpdateSuccess ||'Update Success',
        "AddSuccess": localized.ProductSalesCodeAddSuccess ||'Add Success'
    };

    useEffect(() => {
        getProductSalesCodes();
    }, []);

    const customToolbar = () => {
        return (
            <GridToolbarContainer>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
                {renderAddButton()}
            </GridToolbarContainer>
        );
    }

    const getProductSalesCodes = () => {
        setFetching(true);
        Storefront.getProductSalesCodes().then(loaded);
    }

    const updateProductSalesCode = async (form, type) => {
        var request = {
            id: form.Id ?? 0,
            code: form.code ?? null,
            operationType: type
        };
        return await Storefront.updateProductSalesCode(request).then((res) => {
            if (res) {
                const notificationType = res.response.endsWith("Success") ? "message" : "error";
                const message = responseMessages[res.response];

                if (message) {
                    Notifications[notificationType](message);
                }
                else {
                    Notifications.error(localized.ProductSalesCodeFailUpdateNotification ||'Fail Update');
                }
                getProductSalesCodes();
            }
        });
    }

    const loaded = (data) => {
        if (data) {
            mapRows(data);
        }
        setFetching(false);
    }

    const mapRows = (data) => {
        const rows = [];
        (data.productSalesCode || []).forEach((psc, index) => {
            rows.push({ id: index, codeId: psc.id, code: psc.code });
        });
        setRows(rows);
    };


    const handleClickOpenUpdate = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setOpenUpdate(true);
    };

    const handleCloseUpdate = () => {
        setOpenUpdate(false);
    };
    const handleClickOpenDelete = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleClickOpenAdd = () => {
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const renderDeleteButton = (row) => (
        <MUIAlertModal
            open={openDelete}
            onClose={handleCloseDelete}
            title={localized.AreYouSure}
            message={
                <>
                    <h3>{localized.AreYouSureDeleteProductSalesCode || 'Are you sure you want to delete this Product Sales Code?'}</h3>
                    <DialogContentText>
                        <b>{row.code}</b>
                    </DialogContentText>
                </>
            }
            confirmLabel={localized.StorefrontAdminDelete}
            cancelLabel={localized.StorefrontAdminCancel}
            onConfirm={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("Id", row.codeId);
                const formJson = Object.fromEntries(formData.entries());
                updateProductSalesCode(formJson, "delete");
                handleCloseDelete();
            }}
            confirmButtonProps={{ color: "error" }}
            cancelButtonProps={{ color: "update" }}
        />
    );

    const renderUpdateButton = (row) => (
        <MUIModal
            open={openUpdate}
            onClose={handleCloseUpdate}
            title={localized.UpdateProductSalesCode || 'Update Product Sales Codes'}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("Id", row.codeId);
                formData.append("Code", row.code);
                const formJson = Object.fromEntries(formData.entries());
                updateProductSalesCode(formJson, "update");
                handleCloseUpdate();
            }}
            submitLabel={localized.StorefrontAdminUpdate}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "update" }}
            cancelButtonProps={{ color: "update" }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContentText>
                <h3>{localized.ValuesEligibleForUpdateBelow}</h3>
            </DialogContentText>

            <TextField
                defaultValue={row.code}
                inputProps={{ maxLength: 500 }}
                required
                margin="dense"
                id="code"
                name="code"
                label="Code"
                type="text"
                fullWidth
                variant="standard"
            />
        </MUIModal>
    );

    const renderAddButton = () => (
        <strong>
            <Button
                variant="contained"
                color="success"
                style={{ marginLeft: 16 }}
                onClick={handleClickOpenAdd}
            >
                {localized.StorefrontAdminAdd}
            </Button>
        </strong>
    );


    const renderAddModal = () => (
        <MUIModal
            open={openAdd}
            onClose={handleCloseAdd}
            title={localized.AddProductSalesCode ||'Add Product Sales Codes'}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries(formData.entries());
                updateProductSalesCode(formJson, "add");
                handleCloseAdd();
            }}
            submitLabel={localized.StorefrontAdminAdd}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "success" }}
            cancelButtonProps={{ color: "error" }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContentText>
                <h3>{localized.FillRequiredFieldsBelow}</h3>
            </DialogContentText>

            <TextField
                inputProps={{ maxLength: 500 }}
                required
                id="code"
                name="code"
                label={localized.StorefrontAdminCode}
                type="text"
                fullWidth
                variant="standard"
            />
        </MUIModal>
    );


    const columns = [
        { field: 'codeId', headerName: localized.StorefrontAdminid || 'Id' },
        { field: 'code', headerName: localized.StorefrontAdminCode || 'Code', flex: 1, headerAlign: 'center', align: 'center' },
        {
            field: 'updateButton',
            headerName: localized.StorefrontAdminUpdate,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="update"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenUpdate(e, params.row)}
                        >
                            {localized.StorefrontAdminUpdate}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true

        },
        {
            field: 'deleteButton',
            headerName: localized.StorefrontAdminDelete,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'columnTopper',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="error"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenDelete(e, params.row)}
                        >
                            {localized.StorefrontAdminDelete}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true
        }
    ];

    const productItems = useMemo(() => {
        return (
            <Paper sx={{ height: 700, width: '100%' }}>
                <TranslatableDataGrid
                    rows={rows}
                    columns={columns}
                    insetToolbar={customToolbar}
                    labels={localized}
                    columnVisibilityModel={{ codeId: false }}
                    getRowId={(row) => row.codeId}
                />

            </Paper>
        );
    }, [rows, localized]);

    return (
        <>
            {
                fetching && <Loading type="brand" />
            }
            {
                !fetching && (localized && Object.keys(localized).length > 0) &&
                <>
                    <ThemeProvider theme={theme}>
                        <Typography variant="h1">{localized.ProductSalesCodeAdministrationHeader}</Typography>
                        {productItems}
                        {openUpdate && renderUpdateButton(selectedRow)}
                        {openDelete && renderDeleteButton(selectedRow)}
                        {openAdd && renderAddModal()}
                    </ThemeProvider>
                </>
            }
        </>
    );
}

export default ProductSalesCodeAdministration;

